import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent, TruncatedText, brandFillClass } from "@/components/ui";
import { BackLink, ClickToCopyId } from "@/components/common";
import { Ticket } from "lucide-react";
import { STATUS_CONFIG, type TicketStatus, type TicketPriority } from "./config";
import {
  TicketStatusBadge,
  TicketPriorityBadge,
} from "./TicketBadges";

export interface TicketDetailHeaderCardProps {
  backFallback: string;
  backAriaLabel: string;
  ticketNumber: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  showPriority?: boolean;
  categoryEmoji: string;
  categoryLabel: string;
  openedOnLabel: string;
  action?: ReactNode;
}

export function TicketDetailHeaderCard({
  backFallback,
  backAriaLabel,
  ticketNumber,
  subject,
  status,
  priority,
  showPriority = true,
  categoryEmoji,
  categoryLabel,
  openedOnLabel,
  action,
}: TicketDetailHeaderCardProps) {
  const StatusIcon = STATUS_CONFIG[status]?.icon ?? Ticket;
  return (
    <Card className="shrink-0 animate-slide-up overflow-hidden">
      <CardContent className="px-4 py-4 sm:px-5">
        <div className="flex items-start gap-3 sm:gap-4">
          <BackLink
            variant="icon"
            fallback={backFallback}
            className="-ml-1 shrink-0"
            aria-label={backAriaLabel}
          />
          <div
            className={cn("hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-sm sm:flex", brandFillClass("primary"))}
            aria-hidden
          >
            <StatusIcon className="h-6 w-6" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <ClickToCopyId value={ticketNumber} className="text-[11px]" />
              <TicketStatusBadge status={status} size="sm" />
              {showPriority ? (
                <TicketPriorityBadge priority={priority} size="sm" />
              ) : null}
            </div>
            <TruncatedText
              as="h1"
              className="mt-1.5 text-xl font-semibold tracking-tight text-surface-900 sm:text-2xl"
            >
              {subject}
            </TruncatedText>
            <p className="mt-0.5 text-[13px] text-surface-500">
              {categoryEmoji} {categoryLabel} · {openedOnLabel}
            </p>
          </div>

          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      </CardContent>
    </Card>
  );
}
